/**
 * Register Page Component - Phoenix Premium Design
 *
 * Registration form with admin approval workflow.
 * Email verification disabled - users pay for credits so they have incentive to use real emails.
 * Brand color: #00468C (Phoenix blue)
 *
 * Based on: docs/design/examples/02_register.html
 */

import React, { useState } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../services/firebase';
import { createUserDocument } from '../../services/userService';
import { notifyNewRegistration } from '../../services/api';
import { Link } from 'react-router-dom';

// Icons
const LogoIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
    <path d="M2 17l10 5 10-5" fill="none" stroke="currentColor" strokeWidth="2"/>
    <path d="M2 12l10 5 10-5" fill="none" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);


const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <line x1="19" y1="12" x2="5" y2="12"/>
    <polyline points="12 19 5 12 12 5"/>
  </svg>
);

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    localAssociation: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Jméno je povinné';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Příjmení je povinné';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      newErrors.email = 'Zadejte platnou emailovou adresu';
    }

    if (!formData.password || formData.password.length < 8) {
      newErrors.password = 'Heslo musí mít alespoň 8 znaků';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Hesla se neshodují';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // Create user document in Firestore with 'pending' status
      await createUserDocument(
        userCredential.user.uid,
        formData.email,
        formData.firstName,
        formData.lastName,
        formData.localAssociation
      );

      // Email verification disabled - causes spam folder issues
      // Users pay for credits so they have incentive to use real emails

      // Notify admin about new registration
      const fullName = `${formData.firstName} ${formData.lastName}`;
      try {
        await notifyNewRegistration(
          formData.email,
          fullName,
          formData.localAssociation || undefined
        );
        console.log('[RegisterPage] Admin notification sent successfully');
      } catch (notifyErr) {
        // Log but don't block registration if notification fails
        console.error('[RegisterPage] Admin notification failed:', notifyErr);
      }

      setSuccess(true);
    } catch (err: any) {
      console.error('Registration error:', err);
      if (err.code === 'auth/email-already-in-use') {
        setErrors({ email: 'Email je již registrován' });
      } else if (err.code === 'auth/weak-password') {
        setErrors({ password: 'Heslo je příliš slabé' });
      } else {
        setErrors({ general: err.message || 'Chyba při registraci' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Password strength indicator
  const getPasswordStrength = (password: string): { strength: number; label: string; color: string } => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    if (strength <= 1) return { strength, label: 'Slabé', color: 'var(--danger-500)' };
    if (strength === 2) return { strength, label: 'Střední', color: 'var(--warning-500)' };
    if (strength >= 3) return { strength, label: 'Silné', color: 'var(--success-500)' };
    return { strength: 0, label: '', color: '' };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  // Success screen
  if (success) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-8"
        style={{ background: 'linear-gradient(135deg, var(--phoenix-700) 0%, var(--phoenix-900) 100%)' }}
      >
        {/* Decorative circles */}
        <div className="absolute w-[600px] h-[600px] top-[-200px] right-[-100px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />
        <div className="absolute w-[400px] h-[400px] bottom-[-100px] left-[-50px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />

        <div
          className="w-full max-w-md p-12 rounded-3xl relative z-10 text-center"
          style={{ background: 'var(--bg-secondary)', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
        >
          {/* Success Icon */}
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ background: 'var(--success-100)' }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="var(--success-600)" strokeWidth="2" className="w-10 h-10">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          </div>

          <h2 className="font-display text-2xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            Registrace úspěšná!
          </h2>

          <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
            Váš účet <strong>{formData.email}</strong> byl vytvořen a čeká na schválení administrátorem.
          </p>

          <div
            className="p-4 rounded-xl mb-4"
            style={{ background: 'var(--warning-50)', border: '1px solid var(--warning-200)' }}
          >
            <p className="text-sm" style={{ color: 'var(--warning-700)' }}>
              ⏳ <strong>Čeká na schválení</strong><br/>
              Jakmile bude váš účet schválen, budete moci používat aplikaci.
              O schválení budete informováni emailem.
            </p>
          </div>


          <Link
            to="/login"
            className="btn-phoenix btn-phoenix-primary w-full"
          >
            Přejít na přihlášení
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-8 relative overflow-x-hidden"
      style={{ background: 'linear-gradient(135deg, var(--phoenix-700) 0%, var(--phoenix-900) 100%)' }}
    >
      {/* Decorative circles */}
      <div className="absolute w-[600px] h-[600px] top-[-200px] right-[-100px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />
      <div className="absolute w-[400px] h-[400px] bottom-[-100px] left-[-50px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />

      <div
        className="w-full max-w-md p-12 rounded-3xl relative z-10"
        style={{ background: 'var(--bg-secondary)', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          {/* Logo */}
          <div className="inline-flex items-center gap-3 mb-6">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center text-white"
              style={{
                background: 'linear-gradient(135deg, var(--phoenix-500), var(--phoenix-700))',
                boxShadow: '0 4px 12px rgba(0, 70, 140, 0.3)'
              }}
            >
              <LogoIcon />
            </div>
            <span className="font-display text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
              Phoenix Analytics
            </span>
          </div>

          <h1 className="font-display text-[1.75rem] font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
            Vytvořte si účet
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9375rem' }}>
            Začněte analyzovat volební data s AI
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Name Row */}
          <div className="flex gap-4 mb-5">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                Jméno
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={`input-phoenix ${errors.firstName ? 'error' : ''}`}
                placeholder="Jan"
              />
              {errors.firstName && (
                <div className="flex items-center gap-1 mt-1.5 text-xs" style={{ color: 'var(--danger-600)' }}>
                  <AlertIcon />
                  {errors.firstName}
                </div>
              )}
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                Příjmení
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className={`input-phoenix ${errors.lastName ? 'error' : ''}`}
                placeholder="Novák"
              />
              {errors.lastName && (
                <div className="flex items-center gap-1 mt-1.5 text-xs" style={{ color: 'var(--danger-600)' }}>
                  <AlertIcon />
                  {errors.lastName}
                </div>
              )}
            </div>
          </div>

          {/* Místní sdružení */}
          <div className="mb-5">
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
              Místní sdružení <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>(volitelné)</span>
            </label>
            <input
              type="text"
              name="localAssociation"
              value={formData.localAssociation}
              onChange={handleChange}
              className="input-phoenix"
              placeholder="např. MS Praha 5, MS Brno-střed"
            />
            <p className="text-xs mt-1.5" style={{ color: 'var(--text-tertiary)' }}>
              Zadejte název místního sdružení, za které kandidujete
            </p>
          </div>

          {/* Email */}
          <div className="mb-5">
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
              Emailová adresa
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`input-phoenix ${errors.email ? 'error' : ''}`}
              placeholder="jan.novak@email.cz"
            />
            {errors.email && (
              <div className="flex items-center gap-1 mt-1.5 text-xs" style={{ color: 'var(--danger-600)' }}>
                <AlertIcon />
                {errors.email}
              </div>
            )}
          </div>

          {/* Password */}
          <div className="mb-5">
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
              Heslo
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`input-phoenix pr-12 ${errors.password ? 'error' : ''}`}
                placeholder="Min. 8 znaků"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors"
                style={{ color: 'var(--text-tertiary)' }}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>

            {/* Password strength */}
            {formData.password && (
              <div className="mt-2">
                <div className="flex gap-1 mb-1">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="h-1 flex-1 rounded-full transition-colors"
                      style={{
                        background: i <= passwordStrength.strength ? passwordStrength.color : 'var(--border-light)'
                      }}
                    />
                  ))}
                </div>
                <p className="text-xs" style={{ color: passwordStrength.color }}>
                  {passwordStrength.label}
                </p>
              </div>
            )}

            {errors.password && (
              <div className="flex items-center gap-1 mt-1.5 text-xs" style={{ color: 'var(--danger-600)' }}>
                <AlertIcon />
                {errors.password}
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div className="mb-5">
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
              Potvrdit heslo
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`input-phoenix pr-12 ${errors.confirmPassword ? 'error' : ''}`}
                placeholder="Zadejte heslo znovu"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors"
                style={{ color: 'var(--text-tertiary)' }}
              >
                {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
            {errors.confirmPassword && (
              <div className="flex items-center gap-1 mt-1.5 text-xs" style={{ color: 'var(--danger-600)' }}>
                <AlertIcon />
                {errors.confirmPassword}
              </div>
            )}
          </div>

          {/* General Error */}
          {errors.general && (
            <div
              className="mb-6 px-4 py-3 rounded-xl text-sm flex items-center gap-2"
              style={{
                background: 'var(--danger-100)',
                color: 'var(--danger-600)',
                border: '1px solid var(--danger-500)'
              }}
            >
              <AlertIcon />
              {errors.general}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="btn-phoenix btn-phoenix-primary w-full mb-4"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              'Registrovat se'
            )}
          </button>

          {/* Back to Login */}
          <div className="text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm font-medium transition-colors"
              style={{ color: 'var(--phoenix-500)' }}
            >
              <ArrowLeftIcon />
              Zpět na přihlášení
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
